import Basic from "./Basic.js";
import Map from "../ui/Map.js";
import { NavigationControl } from "maplibre-gl";
import { default as InitParameters, alterMapState } from "../../utils/InitParameters.js";
import { MapTiles } from "../../utils/services.js";
import MapStyleComposer from "../../utils/MapStyleComposer.js";
import CoverageMapStyles from "./CoverageMap.css" with { type: "css" };
document.adoptedStyleSheets.push(CoverageMapStyles);

/**
 * Coverage Map is a basic map showing Panoramax coverage.
 *
 * Make sure to set width/height through CSS for proper display.
 * @class Panoramax.components.core.CoverageMap
 * @element pnx-coverage-map
 * @extends Panoramax.components.core.Basic
 * @fires Panoramax.components.core.Basic#select
 * @fires Panoramax.components.core.Basic#ready
 * @fires Panoramax.components.core.Basic#broken
 * @property {Panoramax.components.ui.Loader} loader The loader screen
 * @property {Panoramax.utils.API} api The API manager
 * @property {Panoramax.components.ui.Map} map The MapLibre GL map itself
 * @property {Panoramax.utils.MapStyleComposer} mapStyleComposer The map style helper
 * @example
 * ```html
 * <pnx-coverage-map
 *   endpoint="https://panoramax.openstreetmap.fr/"
 *   map-options='{"bounds": [[-73.9876, 40.7661], [-73.9397, 40.8002]]}'
 *   style="width: 300px; height: 250px"
 * />
 * ```
 */
export default class CoverageMap extends Basic {
	/**
	 * Component properties.
	 * @memberof Panoramax.components.core.CoverageMap#
	 * @type {Object}
	 * @mixes Panoramax.components.core.Basic#properties
	 * @property {string} endpoint URL to API to use (must be a [STAC API](https://github.com/radiantearth/stac-api-spec/blob/main/overview.md))
	 * @property {string} [picture] The picture ID to display
	 * @property {string} [sequence] The sequence ID of the picture displayed
	 * @property {object} [fetch-options] Set custom options for fetch calls made against API ([same syntax as fetch options parameter](https://developer.mozilla.org/en-US/docs/Web/API/fetch#parameters))
	 * @property {string|object} [map-style] The map's MapLibre style. This can be an a JSON object conforming to the schema described in the [MapLibre Style Specification](https://maplibre.org/maplibre-style-spec/), or a URL string pointing to one. Defaults to OSM vector tiles.
	 * @property {string} [lang] To override language used for labels. Defaults to using user's preferred languages.
	 * @property {object} [map-options] [Any map option available in Map class](#Panoramax.components.ui.Map).<br />Example: `map-options='{"bounds": [[-73.9876, 40.7661], [-73.9397, 40.8002]]}'`
	 * @property {string} [user] To filter on a specific user UUID
	 */
	static properties = {
		"map-options": {converter: Basic.GetJSONConverter()},
		"map-style": {type: String},
		user: {type: String},
		...Basic.properties
	};

	constructor() {
		super();

		this["map-style"] = this.getAttribute("map-style") || MapTiles();

		// Check if mapstyle is not a unparsed JSON
		try {
			this["map-style"] = JSON.parse(this["map-style"]);
		} catch(e) { /* Empty */ }

		this._mapContainer = document.createElement("div");
	}

	/** @private */
	connectedCallback() {
		super.connectedCallback();

		// Setup map styles and sources
		this.mapStyleComposer = new MapStyleComposer(this);
		this.mapStyleComposer.addBasemap("streets", this["map-style"], true);

		this.onceAPIReady().then(() => {
			this.loader.setAttribute("value", 30);
			this._initParams = new InitParameters(InitParameters.GetComponentProperties(CoverageMap, this));
			// eslint-disable-next-line eqeqeq
			const singleUser = this.user && this.user != "";
			this.mapStyleComposer._createPanoramaxEndpointFromAPI("default", this.api, !singleUser);
			if(singleUser) {
				this.mapStyleComposer._createPanoramaxEndpointForUser(this.user);
			}
			this._initMap();
		});
	}

	/** @private */
	attributeChangedCallback(name, _old, value) {
		super.attributeChangedCallback(name, _old, value);

		if(name === "user" && this.api?._isReady === 1) {
			// eslint-disable-next-line eqeqeq
			if(value && value != "") {
				this.mapStyleComposer._createPanoramaxEndpointForUser(value);
			}
			else {
				this.mapStyleComposer.switchPanoramaxEndpoint("default");
			}
		}
	}

	/** @private */
	disconnectedCallback() {
		super.disconnectedCallback();
		this.map?.destroy();
	}

	getClassName() {
		return "CoverageMap";
	}

	onceReady() {
		if(this.map && this.map.waitForEnoughMapLoaded) {
			return this.map.waitForEnoughMapLoaded();
		}
		else {
			return new Promise(resolve => setTimeout(resolve, 100)).then(this.onceReady.bind(this));
		}
	}

	/** @private */
	render() {
		return [this.loader, this._mapContainer];
	}

	getSubComponentsNames() {
		return super.getSubComponentsNames().concat(["map", "mapStyleComposer"]);
	}

	/**
	 * Creates map object
	 * @private
	 */
	_initMap() {
		this.map = new Map(this, this._mapContainer, Object.assign({}, this._initParams.getMapInit(), { hash: true }));
		this.map.addControl(new NavigationControl({ showCompass: false }));
		this.loader.setAttribute("value", 70);

		this.addEventListener("select", this._onSelect.bind(this));
		this.map.on("picture-click", e => this.select(e.seqId, e.picId));
		this.map.on("sequence-click", e => this.select(e.seqId));

		this.map.waitForEnoughMapLoaded().then(() => {
			alterMapState(this.map, this._initParams.getMapPostInit());
			this.loader.dismiss();
		});
	}

	/**
	 * Select event handler
	 * @private
	 * @param {object} e Event details
	 */
	_onSelect(e) {
		// Move thumbnail to match selected element
		if(e.detail.picId || e.detail.seqId) {
			const layer = this.map.getLayersOrder().find(lid => lid.endsWith(e.detail.picId ? "pictures" : "sequences"));
			const features = this.map.queryRenderedFeatures({
				layers: [layer],
				filter: ["==", ["get", "id"], e.detail.picId || e.detail.seqId]
			});

			// eslint-disable-next-line eqeqeq
			if(features.length >= 0 && features[0] != null) {
				this.map._attachPreviewToPictures({ features }, layer);
			}
		} else {
			this.map._dropPreview();
		}
	}
}

customElements.define("pnx-coverage-map", CoverageMap);
